import React from 'react'
import { DataGrid, GridActionsCellItem } from '@mui/x-data-grid'
import { Box, Grid, Button, TextField, Typography, Paper } from '@mui/material'
import { getRequest } from 'Includes/api/apicall'
import { GET_URL } from 'Includes/urls'
import { Link, useNavigate } from 'react-router-dom'
import { AddCircleOutlineOutlined } from '@mui/icons-material'

function CompanyJsonConfigList(props) {
  const navigate = useNavigate()
  const [rows, setRows] = React.useState([])
  const [rowCount, setRowCount] = React.useState(0)
  const [loading, setLoading] = React.useState(false)
  const [page, setPage] = React.useState(0)
  const [pageSize, setPageSize] = React.useState(10)
  const [sortModel, setSortModel] = React.useState([])
  const [searchTerm, setSearchTerm] = React.useState('')
  const [debouncedSearchTerm, setDebouncedSearchTerm] = React.useState('')

  const columns = [
    { field: 'id', headerName: 'ID', width: 90, hide: true },
    { field: 'domain_name', headerName: 'Domain name', width: 260 },
    { field: 'name', headerName: 'Display name', width: 200 },
    { field: 'code', headerName: 'Code', width: 140 },
    { field: 'tel_num', headerName: 'Phone', width: 140 },
    { field: 'email', headerName: 'Email', width: 200 },
    {
      field: 'actions',
      headerName: 'Actions',
      type: 'actions',
      getActions: (params) => [
        <GridActionsCellItem
          label="Edit"
          onClick={() => {
            if (!params?.row?.id) return
            navigate('/company-json-config', { state: { detail: params.row.id } })
          }}
          showInMenu
        />,
      ],
    },
  ]

  React.useEffect(() => {
    const t = setTimeout(() => setDebouncedSearchTerm(searchTerm), 400)
    return () => clearTimeout(t)
  }, [searchTerm])

  const loadRows = () => {
    setLoading(true)
    const params = {
      limit: pageSize,
      pageno: page + 1,
      page_size: pageSize,
      page: page + 1,
    }
    if (debouncedSearchTerm.trim()) {
      params.search = debouncedSearchTerm.trim()
    }
    if (sortModel && sortModel.length > 0) {
      const sort = sortModel[0]
      const field = sort.field
      const sortDir = sort.sort === 'asc' ? '' : '-'
      params.ordering = `${sortDir}${field}`
    }

    getRequest(GET_URL.company_json_config.api, params, props)
      .then((response) => {
        if (response && response.status === 200) {
          const data = response.data?.data || []
          const count =
            response.data?.count ??
            response.data?.total_count ??
            response.data?.total ??
            (Array.isArray(data) ? data.length : 0)
          setRows(Array.isArray(data) ? data : [])
          setRowCount(count)
        }
      })
      .finally(() => setLoading(false))
  }

  React.useEffect(() => {
    setPage(0)
  }, [debouncedSearchTerm])

  React.useEffect(() => {
    loadRows()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchTerm, page, pageSize, sortModel])

  return (
    <Box sx={{ p: 3 }}>
      <Grid container alignItems="center">
        <Grid item xs={12} md={6}>
          <Typography variant="h5" fontWeight={600}>
            Company JSON config
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Login and header flags plus display fields served by the public company config API.
          </Typography>
        </Grid>
        <Grid item xs={12} md={6} textAlign="right">
          <Button
            variant="contained"
            component={Link}
            to="/company-json-config"
            startIcon={<AddCircleOutlineOutlined />}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              px: 2,
              py: 1,
              boxShadow: '0 4px 14px rgba(0,0,0,0.1)',
            }}
          >
            Add config
          </Button>
        </Grid>
      </Grid>

      <Paper sx={{ mt: 3, p: 2, borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
        <TextField
          fullWidth
          size="small"
          label="Search"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Company, name, or code"
        />
      </Paper>

      <Paper sx={{ mt: 3, p: 2, borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
        <DataGrid
          rows={rows}
          columns={columns}
          loading={loading}
          pagination
          paginationMode="server"
          paginationModel={{ page, pageSize }}
          onPaginationModelChange={(model) => {
            if (model.pageSize !== pageSize) {
              setPageSize(model.pageSize)
              setPage(0)
            } else if (model.page !== page) {
              setPage(model.page)
            }
          }}
          pageSizeOptions={[10, 20, 50]}
          rowCount={rowCount}
          sortingMode="server"
          sortModel={sortModel}
          onSortModelChange={(m) => {
            setSortModel(m)
            setPage(0)
          }}
          autoHeight
          rowHeight={42}
          sx={{
            border: 'none',
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: '#f5f7fb',
              fontWeight: 600,
            },
            '& .MuiDataGrid-row:hover': {
              backgroundColor: '#fafafa',
            },
            '& .MuiDataGrid-cell': {
              borderBottom: '1px solid #f0f0f0',
            },
          }}
        />
      </Paper>
    </Box>
  )
}

export default CompanyJsonConfigList
